// Lightweight global error bus for persistence failures.
//
// Why: every autosave hook used to swallow errors with `console.error`, so a
// failed save (session expired, backend down, 409 anti-wipe guard) looked
// exactly like a successful one. The user kept editing, believing the data was
// stored, and lost it on reload.
//
// Any hook/page that persists data calls `reportSaveError(scope, err)` on
// failure and `reportSaveOk(scope)` once a save succeeds again. `<SaveErrorBanner/>`
// (rendered once in App.tsx) subscribes and shows a persistent, dismissible
// warning while at least one scope is failing.

export type SaveErrorScope =
  | 'config'
  | 'musicians'
  | 'crew'
  | 'stageplots'
  | 'bands'
  | 'show'
  | 'setlist'
  | 'songbook'
  | 'songCharts'
  | 'rider'
  | 'daysheet'
  | 'team'
  | 'finance'
  | 'settings'
  | 'venue'
  | 'contact'
  | 'tourbook'
  | 'ai'
  | 'checklists';

export interface SaveErrorEntry {
  scope: SaveErrorScope;
  message: string;
  at: number;
}

type Listener = (entries: SaveErrorEntry[]) => void;

// ---------------------------------------------------------------- showcase --
// The showcase (demo) account is strictly READ-ONLY: the server blocks every
// write and the client cuts the autosave. When this flag is on, reporting a
// save failure is a no-op (there is no save to fail, so no "⚠️ Not saved"
// banner), and every autosave hook must skip its write. Set from AuthContext
// off `/api/auth/me` (server-side `users.is_showcase`).
let showcaseMode = false;

/** True when the signed-in account is the read-only showcase (demo) account. */
export function isShowcaseMode(): boolean {
  return showcaseMode;
}

/** Set by the auth provider whenever the current user changes. */
export function setShowcaseMode(on: boolean): void {
  showcaseMode = !!on;
}

const entries = new Map<SaveErrorScope, SaveErrorEntry>();
const listeners = new Set<Listener>();

function emit() {
  const snapshot = [...entries.values()].sort((a, b) => b.at - a.at);
  listeners.forEach((l) => l(snapshot));
}

function toMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Unknown error';
}

/**
 * An authentication failure (401) is NOT a data-loss event: the app already
 * bounces to /login through `setUnauthorizedHandler`. Reporting it here painted
 * a false "⚠️ Not saved — your latest changes were NOT stored" banner on the
 * LOGIN page (the band list is fetched on mount, before any session exists —
 * see useProfileContext), and because nothing cleared it, the banner was still
 * on screen after logging in: it looked like every login failed. (2026-09-24)
 */
export function isAuthError(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status;
  if (status === 401) return true;
  return /\bAPI 401\b|\bunauthorized\b/i.test(toMessage(err));
}

/** Record a persistence failure for a scope (replaces any previous one).
 *  No-op for the showcase (demo) account: nothing is ever saved there, so a
 *  failure cannot happen — reporting one would show a bogus "Not saved".
 *  No-op for authentication failures — see isAuthError. */
export function reportSaveError(scope: SaveErrorScope, err: unknown): void {
  if (showcaseMode) return;
  if (isAuthError(err)) return;
  entries.set(scope, { scope, message: toMessage(err), at: Date.now() });
  emit();
}

/** Clear the failure for a scope — call after a save succeeds. */
export function reportSaveOk(scope: SaveErrorScope): void {
  if (entries.delete(scope)) emit();
}

/** Clear every recorded failure (used by the banner's dismiss button). */
export function clearSaveErrors(): void {
  if (entries.size === 0) return;
  entries.clear();
  emit();
}

/** Subscribe to the current failure list. Returns an unsubscribe function. */
export function subscribeSaveErrors(listener: Listener): () => void {
  listeners.add(listener);
  listener([...entries.values()].sort((a, b) => b.at - a.at));
  return () => {
    listeners.delete(listener);
  };
}
